// Phase DELHIVERY-1 (HYBRID) — Public tracking page (CSR React).
//
// Routes:
//   /track                  → search box
//   /track/:tracking_id     → fetches GET /api/public/track/{id}
//
// SECURITY: The endpoint NEVER exposes the courier AWB. This page renders
// only what the endpoint returns. If a future API change reintroduces an
// AWB, the page does NOT have a dedicated render path for it.
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Package, Truck, MapPin, Clock, ChevronRight, RefreshCw, Search } from "lucide-react";
import OnTimeBadge from "../../components/OnTimeBadge";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Fallback skeleton steps shown when the parcel hasn't been handed to a
// courier yet (or as a "ghost" guide once the journey starts).
const SKELETON_STAGES = [
  { key: "order_placed",      label: "Order placed",          source: "pickngo" },
  { key: "packed",            label: "Packed at PickNGo hub", source: "pickngo" },
  { key: "handed_to_courier", label: "Handed to courier",     source: "pickngo" },
  { key: "in_transit",        label: "In transit",            source: "courier" },
  { key: "out_for_delivery",  label: "Out for delivery",      source: "courier" },
  { key: "delivered",         label: "Delivered",             source: "courier" },
];

function _relative(ts) {
  if (!ts) return "";
  const dt = new Date(ts);
  const diff = Date.now() - dt.getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return dt.toLocaleString("en-IN", { dateStyle: "medium" });
}

function StatusPill({ label, source }) {
  const cn = source === "courier"
    ? "bg-violet-100 text-violet-800 border-violet-200"
    : "bg-orange-100 text-orange-800 border-orange-200";
  return (
    <span className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded border ${cn}`}>
      via {source === "courier" ? "Courier" : "PickNGo"}
    </span>
  );
}

function TimelineItem({ ev, isLast, isGhost }) {
  return (
    <li className="relative pl-8 pb-6" data-testid={`timeline-event-${ev.event || ev.key}`}>
      <span className={`absolute left-2 top-1.5 h-3 w-3 rounded-full border-2 ${
        isGhost ? "bg-slate-100 border-slate-300" :
        ev.source === "courier" ? "bg-violet-500 border-violet-500" : "bg-orange-500 border-orange-500"
      }`} />
      {!isLast && <span className="absolute left-3 top-4 bottom-0 w-px bg-slate-200" />}
      <div className={isGhost ? "opacity-40" : ""}>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-slate-800">{ev.label}</span>
          <StatusPill source={ev.source} />
        </div>
        {ev.location && (
          <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> {ev.location}
          </div>
        )}
        {ev.timestamp && (
          <div className="text-xs text-slate-400 mt-0.5" title={new Date(ev.timestamp).toLocaleString("en-IN")}>
            <Clock className="h-3 w-3 inline mr-1" />
            {_relative(ev.timestamp)}
          </div>
        )}
      </div>
    </li>
  );
}

function TrackContent({ data, onRefresh, refreshing }) {
  const partner = data.courier_partner;
  const events = data.timeline || [];
  const seenKeys = new Set(events.map((e) => e.event));
  // Ghost-render the canonical journey for stages we haven't reached yet.
  const ghostEvents = SKELETON_STAGES
    .filter((s) => !seenKeys.has(s.key))
    .filter((s) => {
      // Hide pickngo ghosts that come BEFORE the latest pickngo event;
      // show courier-side ghosts only when partner is set OR no events yet.
      if (s.source === "courier") return true;
      return false;
    });

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200" data-testid="public-tracking-card">
      <header className="px-5 sm:px-7 pt-5 pb-4 border-b border-slate-100">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-slate-500 text-xs uppercase tracking-wider font-semibold">
            <Package className="h-3.5 w-3.5 text-orange-500" />
            <span>Tracking</span>
            <span className="font-mono text-slate-700 text-sm normal-case tracking-normal"
                  data-testid="public-tracking-id">{data.pickngo_tracking_id}</span>
          </div>
          <button onClick={onRefresh} title="Refresh"
                  data-testid="public-tracking-refresh"
                  className="p-1.5 hover:bg-slate-100 rounded text-slate-500">
            <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </header>

      <section className="px-5 sm:px-7 py-5">
        <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" data-testid="public-tracking-status">
          {data.current_status_display || "Status unknown"}
        </h1>
        <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-slate-600">
          {partner && (
            <span className="inline-flex items-center gap-1 bg-violet-50 text-violet-700 px-2 py-0.5 rounded-full border border-violet-100"
                  data-testid="public-tracking-partner">
              <Truck className="h-3 w-3" /> via {partner}
            </span>
          )}
          {data.last_scan_location && (
            <span className="inline-flex items-center gap-1 text-slate-500"
                  data-testid="public-tracking-location">
              <MapPin className="h-3 w-3" /> {data.last_scan_location}
            </span>
          )}
          {data.eta_iso && (
            <span className="inline-flex items-center gap-1 text-slate-500"
                  data-testid="public-tracking-eta">
              <Clock className="h-3 w-3" /> ETA {new Date(data.eta_iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </span>
          )}
        </div>
      </section>

      <section className="px-5 sm:px-7 pb-7">
        <h2 className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-4">
          Journey
        </h2>
        <ol data-testid="public-tracking-timeline">
          {events.map((ev, i) => (
            <TimelineItem key={`${ev.event}-${i}`} ev={ev} isLast={false} isGhost={false} />
          ))}
          {!partner && ghostEvents.map((g, i) => (
            <TimelineItem key={`ghost-${g.key}`} ev={g} isLast={i === ghostEvents.length - 1} isGhost={true} />
          ))}
        </ol>
      </section>
    </div>
  );
}

export default function PublicTrackingPage() {
  const { tracking_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");

  const load = useCallback(async (silent = false) => {
    if (!tracking_id) return;
    if (!silent) setLoading(true);
    try {
      const r = await fetch(`${BACKEND}/api/public/track/${encodeURIComponent(tracking_id)}`,
                            { cache: "no-store" });
      if (r.status === 404) { setError({ code: 404 }); return; }
      if (!r.ok) { setError({ code: r.status }); return; }
      const body = await r.json();
      setData(body);
      setError(null);
    } catch (e) {
      setError({ code: 0, msg: String(e) });
    } finally {
      setLoading(false);
    }
  }, [tracking_id]);

  useEffect(() => {
    if (tracking_id) load(false);
  }, [tracking_id, load]);

  useEffect(() => {
    if (!tracking_id) return;
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [tracking_id, load]);

  if (!tracking_id) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="public-tracking-search-page">
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-7">
            <div className="flex items-center gap-2 text-orange-500 font-semibold tracking-tight mb-3">
              <Package className="h-5 w-5" /> PickNGo
            </div>
            <h1 className="text-2xl font-semibold text-slate-900">Track your parcel</h1>
            <p className="text-sm text-slate-500 mt-1">Enter your PickNGo tracking ID below.</p>
            <div className="mt-3">
              <OnTimeBadge />
            </div>
            <form
              className="mt-5 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const v = (searchInput || "").trim();
                if (v) navigate(`/track/${encodeURIComponent(v)}`);
              }}
            >
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="e.g. PKG-A1B2C3D4"
                data-testid="public-tracking-search-input"
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-300"
              />
              <button type="submit" data-testid="public-tracking-search-submit"
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium inline-flex items-center gap-1">
                <Search className="h-4 w-4" /> Track
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="px-5 sm:px-7 py-4 bg-white border-b border-slate-100">
        <Link to="/" className="inline-flex items-center gap-2 text-orange-500 font-semibold tracking-tight">
          <Package className="h-5 w-5" /> PickNGo
          <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
          <span className="text-slate-700 text-sm font-normal">Tracking</span>
        </Link>
      </header>

      <main className="max-w-2xl mx-auto p-5 sm:p-7">
        {loading && !data && (
          <div className="bg-white rounded-2xl p-7 border border-slate-200" data-testid="public-tracking-loading">
            <div className="animate-pulse space-y-3">
              <div className="h-4 w-32 bg-slate-100 rounded" />
              <div className="h-8 w-56 bg-slate-100 rounded" />
              <div className="h-3 w-40 bg-slate-100 rounded" />
            </div>
          </div>
        )}

        {error && error.code === 404 && (
          <div className="bg-white rounded-2xl border border-rose-200 p-7" data-testid="public-tracking-404">
            <h1 className="text-xl font-semibold text-rose-700">We couldn't find that tracking ID</h1>
            <p className="text-sm text-rose-600 mt-2">
              Double-check the ID and try again. Tracking IDs look like <code className="font-mono">PKG-XXXXXXXX</code>.
            </p>
            <Link to="/track"
                  data-testid="public-tracking-back-to-search"
                  className="inline-block mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm">
              Search again
            </Link>
          </div>
        )}

        {error && error.code !== 404 && (
          <div className="bg-white rounded-2xl border border-rose-200 p-7" data-testid="public-tracking-error">
            <h1 className="text-xl font-semibold text-rose-700">Couldn't load tracking</h1>
            <p className="text-sm text-rose-600 mt-2">Network or server issue. Please retry.</p>
            <button onClick={() => load(false)}
                    data-testid="public-tracking-retry"
                    className="mt-4 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm">
              Retry
            </button>
          </div>
        )}

        {data && !error && (
          <TrackContent data={data} onRefresh={() => load(false)} refreshing={loading} />
        )}
      </main>
    </div>
  );
}
